import type { ElementType, HTMLAttributes, MouseEvent } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";
import {
  polymorphicForwardRef,
  Text,
  type TrackingProps,
  useTracker,
} from "@huub-web/ui";

import {
  stickerWrapperStyle,
  stickerStyle,
  stickerTextStyle,
  foldStyle,
} from "./Sticker.css";

/**
 * Props for the Sticker component
 */
export type StickerProps<
  T extends HTMLAttributes<Element> = HTMLAttributes<HTMLDivElement>,
> = T &
  RecipeVariants<typeof stickerStyle> &
  RecipeVariants<typeof stickerWrapperStyle> &
  TrackingProps & {
    as?: ElementType;
    value: string;
    showFold?: boolean;
  };

/**
 * Sticker component that displays text in a circular badge with optional fold effect.
 * The sticker is absolutely positioned within its parent container.
 * @deprecated
 */
export const Sticker = polymorphicForwardRef<"div", StickerProps>(
  (
    {
      value,
      variant,
      showFold = true,
      position,
      className,
      as: Element = "div",
      tracking,
      onClick,
      ...props
    },
    forwardedRef,
  ) => {
    const { track } = useTracker();

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
      if (Element !== "button" && Element !== "a") {
        return;
      }

      onClick?.(e);

      if (tracking) {
        track("interaction", {
          category: "button",
          action: "click",
          label: tracking,
          properties: {
            buttonId: tracking,
            buttonText: typeof value === "string" ? value : undefined,
            buttonType: "sticker",
          },
        });
      }
    };

    return (
      <Element
        ref={forwardedRef}
        className={mergeClasses(
          stickerWrapperStyle({
            position,
            hasHover: Element === "button" || Element === "a",
          }),
          className,
        )}
        onClick={handleClick}
        {...props}
      >
        <div className={stickerStyle({ variant })}>
          <Text className={stickerTextStyle} bold align="center">
            {value}
          </Text>
          {showFold && <div className={foldStyle({ variant })} />}
        </div>
      </Element>
    );
  },
);

Sticker.displayName = "Sticker";
